import { z } from 'zod';
export const ApiError = z.object({
    statusCode: z.number(),
    error: z.string(),
    message: z.string(),
});
export const Health = z.object({ ok: z.boolean(), db: z.boolean() });
// ---- meta -------------------------------------------------------------------
export const IndicatorMeta = z.object({
    indicator: z.string(),
    units: z.string().nullable(),
    inverted: z.boolean().describe('true = lower is better'),
    description: z.string().nullable().optional(),
    formula: z.string().nullable().optional(),
    unit: z.string().nullable().optional(),
});
export const ComponentGroup = z.object({
    component: z.string(),
    dimension: z.string(),
    indicators: z.array(IndicatorMeta),
});
export const Region = z.object({ code: z.string(), label: z.string() });
export const Meta = z.object({
    indicators: z.array(ComponentGroup),
    regions: z.array(Region),
    income_groups: z.array(z.string()),
    ownerships: z.array(z.string()),
    types: z.array(z.string()),
    years: z.object({ from: z.number().int(), to: z.number().int() }),
    default_indicator: z.string(),
});
// ---- shared pieces ------------------------------------------------------------
const refYear = z
    .number()
    .int()
    .nullable()
    .describe('Most recent year with at least half the coverage of the best-covered year; null when no data');
const indicatorContext = {
    indicator: z.string(),
    units: z.string().nullable(),
    inverted: z.boolean(),
    description: z.string().nullable().optional(),
    formula: z.string().nullable().optional(),
    unit: z.string().nullable().optional(),
};
// ---- summary -----------------------------------------------------------------
export const Summary = z.object({
    ...indicatorContext,
    utilities: z.number().int(),
    countries: z.number().int(),
    observations: z.number().int(),
    ref_year: refYear,
    median_ref_year: z.number().nullable(),
});
// ---- by-region -----------------------------------------------------------------
export const RegionStat = z.object({
    region: z.string(),
    label: z.string(),
    median: z.number(),
    n: z.number().int(),
});
export const ByRegion = z.object({
    ...indicatorContext,
    ref_year: refYear,
    rows: z.array(RegionStat).describe('Sorted by median, descending'),
});
// ---- distribution ----------------------------------------------------------------
export const Bin = z.object({ x0: z.number(), x1: z.number(), count: z.number().int() });
export const Distribution = z.object({
    ...indicatorContext,
    ref_year: refYear,
    n: z.number().int().describe('Values at ref_year'),
    p01: z.number().nullable(),
    p99: z.number().nullable(),
    n_clipped: z.number().int().describe('Values outside [p01, p99], not in bins'),
    bins: z.array(Bin),
});
// ---- trend --------------------------------------------------------------------------
export const TrendPoint = z.object({
    year: z.number().int(),
    region: z.string(),
    label: z.string(),
    median: z.number(),
    n: z.number().int(),
});
export const Trend = z.object({
    ...indicatorContext,
    rows: z.array(TrendPoint).describe('Sorted by year, then region display order'),
});
// ---- ranking ---------------------------------------------------------------------------
export const RankRow = z.object({
    rank: z.number().int(),
    utility_id: z.number().int(),
    utility: z.string().describe('Long name'),
    country: z.string(),
    region: z.string(),
    ownership: z.string().nullable(),
    type: z.string().nullable(),
    value: z.number(),
});
export const Ranking = z.object({
    ...indicatorContext,
    ref_year: refYear,
    rows: z.array(RankRow).describe('Best first: ascending when inverted, else descending'),
});
// ---- country-profile -------------------------------------------------------------
export const CountryProfileIndicator = z.object({
    indicator: z.string(),
    component: z.string(),
    dimension: z.string(),
    units: z.string().nullable(),
    inverted: z.boolean(),
    description: z.string().nullable().optional(),
    formula: z.string().nullable().optional(),
    unit: z.string().nullable().optional(),
    country_median: z.number(),
    africa_median: z.number().nullable(),
    percentile_rank: z.number().describe('Percentile in Africa (0 to 100) based on raw value'),
    normalized_score: z.number().describe('0 to 100 where 100 is always best performance: flipped when inverted'),
    n_utilities: z.number().int(),
    min_value: z.number(),
    max_value: z.number(),
    utilities: z.array(z.object({
        utility_id: z.number().int(),
        utility: z.string(),
        value: z.number(),
    })),
});
export const CountryProfile = z.object({
    country: z.string(),
    iso3c: z.string().nullable(),
    region: z.string(),
    year: z.number().int(),
    indicators: z.array(CountryProfileIndicator),
});
//# sourceMappingURL=responses.js.map